import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  createClient,
  deleteClient,
  getClientById,
  getClients,
  updateClient,
} from 'data';

interface Address {
  id: number | undefined;
  City: string | null;
  Country: string | null;
  Street: string | null;
  PostCode: string | null;
  HouseNumber: string | null;
}

interface Client {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  created_at: string;
  updated_at: string;
  address: Address | null;
  contactPersonFirstName: string | null;
  contactPersonLastName: string | null;
  contactPersonSalutation: string | null;
}

interface ClientsState {
  clients: Client[];
  selectedClient: Client | null;
  count: number;
  page: number;
  per_page: number;
  getClientsLoading: boolean;
  createClientLoading: boolean;
  updatingClientLoading: boolean;
  deletingClientLoading: boolean;
  clientsError: string | null;
}

// ✅ Fetch clients
export const fetchClients = createAsyncThunk(
  'clients/fetchClients',
  async (
    { page = 1, per_page = 10 }: { page?: number; per_page?: number },
    { rejectWithValue },
  ) => {
    try {
      const response = await getClients();
      return {
        clients: response.data.data,
        count: response.data.count,
        page,
        per_page,
      };
    } catch (clientsError: any) {
      return rejectWithValue(
        clientsError.response?.data || 'Failed to fetch clients',
      );
    }
  },
);

// ✅ Create new client
export const createNewClient = createAsyncThunk(
  'clients/createClient',
  async (clientData: Partial<Client>, { rejectWithValue }) => {
    try {
      const response = await createClient(clientData);
      return response.data;
    } catch (clientsError: any) {
      return rejectWithValue(
        clientsError.response?.data || 'Failed to create client',
      );
    }
  },
);

export const updateClientById = createAsyncThunk(
  'clients/updateClient',
  async (clientData: Partial<Client>, { rejectWithValue }) => {
    try {
      const payload = {
        ...clientData,
        address_id: clientData.address?.id, // Ensure address_id is sent
      };
      if (!clientData.id) {
        throw new Error('Client ID is required');
      }
      const response = await updateClient(payload, clientData.id);
      return response.data;
    } catch (clientsError: any) {
      return rejectWithValue(
        clientsError.response?.data || 'Failed to update client',
      );
    }
  },
);

export const deleteClientById = createAsyncThunk(
  'clients/deleteClientById',
  async (clientId: number, { rejectWithValue }) => {
    try {
      await deleteClient(clientId);
      return clientId; // Return the deleted client ID
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to delete client');
    }
  },
);

export const fetchClientById = createAsyncThunk(
  'clients/fetchClientById',
  async (clientId: number, { rejectWithValue }) => {
    try {
      const response = await getClientById(clientId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || 'Failed to fetch client');
    }
  },
);

const initialState: ClientsState = {
  clients: [],
  selectedClient: null,
  count: 0,
  page: 1,
  per_page: 10,
  getClientsLoading: false,
  createClientLoading: false,
  updatingClientLoading: false,
  clientsError: null,
  deletingClientLoading: false,
};

const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        state.getClientsLoading = true;
      })
      .addCase(
        fetchClients.fulfilled,
        (
          state,
          action: PayloadAction<{
            clients: Client[];
            count: number;
            page: number;
            per_page: number;
          }>,
        ) => {
          state.getClientsLoading = false;
          state.clients = action.payload.clients;
          state.count = action.payload.count;
          state.page = action.payload.page;
          state.per_page = action.payload.per_page;
          state.clientsError = null;
        },
      )
      .addCase(
        fetchClients.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.getClientsLoading = false;
          state.clientsError =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch clients';
        },
      )
      .addCase(createNewClient.pending, (state) => {
        state.createClientLoading = true;
      })
      .addCase(
        createNewClient.fulfilled,
        (state, action: PayloadAction<Client>) => {
          state.createClientLoading = false;
          state.clients.push(action.payload);
          state.clientsError = null;
        },
      )
      .addCase(
        createNewClient.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.createClientLoading = false;
          state.clientsError =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to create client';
        },
      )
      .addCase(updateClientById.pending, (state) => {
        state.updatingClientLoading = true;
      })
      .addCase(updateClientById.fulfilled, (state, action) => {
        const updatedClient = action.payload;
        state.updatingClientLoading = false;
        const index = state.clients.findIndex(
          (client) => client.id === updatedClient.id,
        );
        if (index !== -1) {
          state.clients[index] = updatedClient;
        }
      })
      .addCase(
        updateClientById.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.updatingClientLoading = false;
          state.clientsError =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to update client';
        },
      )
      .addCase(deleteClientById.pending, (state) => {
        state.deletingClientLoading = true;
      })
      .addCase(deleteClientById.fulfilled, (state, action) => {
        state.deletingClientLoading = false;
        state.clients = state.clients.filter(
          (client) => client.id !== action.payload,
        );
      })
      .addCase(deleteClientById.rejected, (state, action) => {
        state.deletingClientLoading = false;
        state.clientsError =
          typeof action.payload === 'string'
            ? action.payload
            : 'Failed to delete client';
      })
      .addCase(fetchClientById.pending, (state) => {
        state.getClientsLoading = true;
      })
      .addCase(
        fetchClientById.fulfilled,
        (state, action: PayloadAction<Client>) => {
          state.getClientsLoading = false;
          state.selectedClient = action.payload;
        },
      )
      .addCase(
        fetchClientById.rejected,
        (state, action: PayloadAction<string | unknown>) => {
          state.getClientsLoading = false;
          state.clientsError =
            typeof action.payload === 'string'
              ? action.payload
              : 'Failed to fetch client';
        },
      );
  },
});

export const clientsReducer = clientsSlice.reducer;
